// Axios instance and types
import http from '../api'
import { APIResponse, APIPaginatedResponse, Filters, Pagination } from '../types'
import { TypeInvoicePayment } from '../../types/Payment'
import { parseDate, dateToIso } from '../utils/utils'

const apiPath = import.meta.env.VITE_API_PATH

type ReceiptInvoicePayload = {
  invoice_uuid: string
}

type ReceiptCreatePayload = {
  client_uuid: string | null
  payment_date: string | null
  payment_method: number
  amount_tendered: number
  change_given: number
  payment_provider_id: string
  reference_no: string
  transaction_id: string
  comment: string
  invoices: ReceiptInvoicePayload[]
}

const toReceiptInvoices = (payment: TypeInvoicePayment): ReceiptInvoicePayload[] => {
  return (payment.invoices ?? [])
    .map((invoice) => String(invoice.uuid ?? '').trim())
    .filter((invoiceUuid) => invoiceUuid !== '')
    .map((invoiceUuid) => ({ invoice_uuid: invoiceUuid }))
}

const buildReceiptPayload = (payment: TypeInvoicePayment): ReceiptCreatePayload => ({
  client_uuid: payment.client_uuid || null,
  payment_date: payment.payment_date ? parseDate('' + payment.payment_date) : null,
  payment_method: Number(payment.payment_method),
  amount_tendered: Number(payment.amount_tendered),
  change_given: Number(payment.change_given),
  payment_provider_id: payment.payment_provider_id || '',
  reference_no: payment.reference_no || '',
  transaction_id: payment.transaction_id || '',
  comment: payment.comment || '',
  invoices: toReceiptInvoices(payment),
})

/**
 * Query string for GET api/receipts.
 * That index reads `search`, `payment_method`, `date_from`, `date_to`, `page`, and `per_page`.
 */
const receiptListQuery = (filters?: Partial<Filters>, pagination?: Partial<Pagination>) => {
  const params = new URLSearchParams()
  const search = filters?.search?.trim()
  if (search) params.set('search', search)

  for (const clause of filters?.filters ?? []) {
    if (clause.column === 'payment_method' && clause.search) {
      params.set('payment_method', clause.search)
    }
    if (clause.column === 'date') {
      const from = dateToIso(clause.start_date)
      const to = dateToIso(clause.end_date)
      if (from) params.set('date_from', from)
      if (to) params.set('date_to', to)
    }
  }

  if (pagination?.page) params.set('page', String(pagination.page))
  if (pagination?.perPage) params.set('per_page', String(pagination.perPage))
  const query = params.toString()
  return query ? `?${query}` : ''
}

/** Receipt routes only. `api/payments/` belongs to the Flutter app. */
async function getReceipts(filters?: Partial<Filters>, pagination?: Partial<Pagination>) {
  return await http.get<APIPaginatedResponse<TypeInvoicePayment>>(
    apiPath + 'receipts' + receiptListQuery(filters, pagination),
  )
}

async function getPaymentByUuid(uuid: string) {
  return await http.get<APIResponse<TypeInvoicePayment>>(apiPath + 'receipts/' + uuid)
}

async function getPaymentByNumber(paymentNumber: string, detailed = false) {
  // detailed=1 asks the API to include invoice lines. It does not create another payment.
  const query = detailed ? '?detailed=1' : ''
  return await http.get<APIResponse<TypeInvoicePayment>>(
    `${apiPath}receipts/${encodeURIComponent(paymentNumber)}${query}`,
  )
}

async function create(payment: TypeInvoicePayment) {
  const payload = buildReceiptPayload(payment)
  return await http.post<APIResponse<any>>(apiPath + 'receipts', payload)
}

async function update(uuid: string, payment: TypeInvoicePayment) {
  const payload = {
    client_uuid: payment.client_uuid,
    invoice_no: payment.invoice_no,
    payment_date: payment.payment_date ? parseDate('' + payment.payment_date) : null,
    payment_method: payment.payment_method,
    invoice_total_amount: payment.invoice_total_amount,
    invoice_total_amount_payable: payment.invoice_total_amount_payable,
    amount_paid: payment.amount_paid,
    amount_tendered: payment.amount_tendered,
    change_given: payment.change_given,
    payment_provider_id: payment.payment_provider_id,
    reference_no: payment.reference_no,
    transaction_id: payment.transaction_id,
    use_loyalty_points: payment.use_loyalty_points,
    loyalty_points: payment.loyalty_points,
    loyalty_amount: payment.loyalty_amount,
    discount_type: payment.discount_type,
    discount_rate: payment.discount_rate,
    discount_amount: payment.discount_amount,
    tax_amount: payment.tax_amount,
    comment: payment.comment,
    items: payment.items,
  }
  return await http.patch<APIResponse<any>>(`${apiPath}receipts/${uuid}`, payload)
}

async function deletePayment(uuid: string) {
  return await http.delete<APIResponse<null>>(`${apiPath}receipts/${uuid}`)
}

async function getPaymentProvidersByPaymentMethod(paymentMethod: number) {
  return await http.get<APIResponse<any>>(`${apiPath}payment-providers/payment-method/${paymentMethod}`)
}

export default {
  getReceipts,
  getPaymentByUuid,
  getPaymentByNumber,
  create,
  update,
  deletePayment,
  getPaymentProvidersByPaymentMethod,
}
